import * as Sentry from '@sentry/browser';
import { browserTracingIntegration } from '@sentry/browser';

type SentryLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal' | 'log';
type BrowserSentryOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

export type SentryInitOptions = {
  appName: string;
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  sendDefaultPii?: boolean;
  enableLogs?: boolean;
  captureConsoleLogs?: boolean;
  enableTracing?: boolean;
  enableReportPageLoaded?: boolean;
  dataCollection?: BrowserSentryOptions['dataCollection'];
  beforeSend?: BrowserSentryOptions['beforeSend'];
  tunnel?: string;
};

export type LogContext = Record<string, unknown>;

export type Logger = {
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: unknown, context?: LogContext) => void;
};

let sentryInitialized = false;
let sentryEnabled = false;
let logsEnabled = false;
let activeAppName = 'unknown-app';

const normalizeLevel = (level: 'debug' | 'info' | 'warn' | 'error'): SentryLevel =>
  level === 'warn' ? 'warning' : level;

export const initSentry = ({
  appName,
  dsn,
  environment,
  release,
  tracesSampleRate,
  sendDefaultPii,
  enableLogs = true,
  captureConsoleLogs = false,
  enableTracing = false,
  enableReportPageLoaded = false,
  dataCollection,
  beforeSend,
  tunnel,
}: SentryInitOptions): boolean => {
  activeAppName = appName;

  if (sentryInitialized) {
    Sentry.setTag('app_name', appName);
    return sentryEnabled;
  }

  if (!dsn || !dsn.trim()) {
    sentryInitialized = true;
    sentryEnabled = false;
    logsEnabled = false;
    return false;
  }

  try {
    Sentry.init({
      dsn,
      environment: environment ?? 'development',
      release,
      tracesSampleRate: enableTracing ? (tracesSampleRate ?? 1.0) : undefined,
      sendDefaultPii,
      dataCollection,
      beforeSend,
      tunnel,
      enableLogs,
      integrations: [
        ...(captureConsoleLogs ? [Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] })] : []),
        ...(enableTracing ? [browserTracingIntegration({
          enableReportPageLoaded,
        })] : []),
      ],
    });
    Sentry.setTag('app_name', appName);
    sentryEnabled = true;
    logsEnabled = enableLogs;
  } catch (error) {
    console.error('[Observability] Failed to initialize Sentry', error);
    sentryEnabled = false;
    logsEnabled = false;
  }

  sentryInitialized = true;
  return sentryEnabled;
};

export const isSentryEnabled = () => sentryEnabled;

export const setSentryUser = (user: { id?: string; email?: string; username?: string } | null) => {
  if (!sentryEnabled) return;
  Sentry.setUser(user);
};

export const setSentryTag = (key: string, value: string) => {
  if (!sentryEnabled) return;
  Sentry.setTag(key, value);
};

export const captureError = (error: unknown, context?: LogContext) => {
  if (!sentryEnabled) return;

  if (!context || Object.keys(context).length === 0) {
    Sentry.captureException(error);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setContext('details', context);
    scope.setTag('app_name', activeAppName);
    Sentry.captureException(error);
  });
};

export const captureMessage = (message: string, level: SentryLevel = 'info', context?: LogContext) => {
  if (!sentryEnabled) return;

  if (!context || Object.keys(context).length === 0) {
    Sentry.captureMessage(message, level);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setContext('details', context);
    scope.setTag('app_name', activeAppName);
    Sentry.captureMessage(message, level);
  });
};

const logToConsole = (
  namespace: string,
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context?: LogContext,
  error?: unknown
) => {
  const line = `[${namespace}] ${message}`;

  if (level === 'error') {
    if (context && error !== undefined) {
      console.error(line, context, error);
    } else if (context) {
      console.error(line, context);
    } else if (error !== undefined) {
      console.error(line, error);
    } else {
      console.error(line);
    }
    return;
  }

  const fn = level === 'warn' ? console.warn : level === 'info' ? console.log : console.debug;
  if (context) {
    fn(line, context);
  } else {
    fn(line);
  }
};

export const createLogger = (namespace: string): Logger => {
  const sendSentryLog = (
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    context?: LogContext
  ) => {
    if (!logsEnabled) return;

    // Don't send debug logs to Sentry
    if (level === 'debug') return;

    const attributes = {
      logger_namespace: namespace,
      app_name: activeAppName,
      ...context,
    };

    if (level === 'info') {
      Sentry.logger.info(message, attributes);
      return;
    }
    if (level === 'warn') {
      Sentry.logger.warn(message, attributes);
      return;
    }
    Sentry.logger.error(message, attributes);
  };

  const addBreadcrumb = (
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    context?: LogContext
  ) => {
    if (!sentryEnabled) return;
    Sentry.addBreadcrumb({
      category: namespace,
      level: normalizeLevel(level),
      message,
      data: context,
    });
  };

  return {
    debug(message, context) {
      logToConsole(namespace, 'debug', message, context);
      addBreadcrumb('debug', message, context);
      sendSentryLog('debug', message, context);
    },
    info(message, context) {
      logToConsole(namespace, 'info', message, context);
      addBreadcrumb('info', message, context);
      sendSentryLog('info', message, context);
    },
    warn(message, context) {
      logToConsole(namespace, 'warn', message, context);
      addBreadcrumb('warn', message, context);
      sendSentryLog('warn', message, context);
    },
    error(message, error, context) {
      logToConsole(namespace, 'error', message, context, error);
      addBreadcrumb('error', message, context);
      sendSentryLog('error', message, context);

      if (error === undefined) {
        captureMessage(`[${namespace}] ${message}`, 'error', context);
        return;
      }

      captureError(error, {
        logger_message: message,
        logger_namespace: namespace,
        ...context,
      });
    },
  };
};
export { reportPageLoaded as reportSentryPageLoaded } from '@sentry/browser';

// ---------------------------------------------------------------------------
// High-level app observability factory
// ---------------------------------------------------------------------------

import { getPublicCommitSha, getPublicSentryDsn } from './publicEnv';

type NetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
};

type NavigatorWithDeviceMemory = Navigator & {
  deviceMemory?: number;
};

const collectDeviceContext = () => {
  try {
    const tags: Record<string, string> = {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };

    const conn = (navigator as NavigatorWithConnection).connection;
    if (conn) {
      if (conn.effectiveType) tags.net_type = conn.effectiveType;
      if (conn.downlink != null) tags.net_downlink = String(conn.downlink);
      if (conn.saveData != null) tags.net_save_data = String(conn.saveData);
    }

    const nav = navigator as NavigatorWithDeviceMemory;
    if (nav.deviceMemory != null) tags.device_memory = String(nav.deviceMemory);
    if (navigator.hardwareConcurrency != null) tags.hw_concurrency = String(navigator.hardwareConcurrency);

    Sentry.setTags(tags);
  } catch {
    // Best-effort
  }
};

export type AppObservabilityOptions = {
  enableSentry?: boolean;
  enableTracing?: boolean;
  enableReportPageLoaded?: boolean;
  tracesSampleRate?: number;
};

export type AppObservability = {
  logger: Logger;
  init: () => void;
  setUser: (userId: string | null) => void;
  captureError: typeof captureError;
};

export const createAppObservability = (
  appName: string,
  options: AppObservabilityOptions = {},
): AppObservability => {
  const logger = createLogger(appName);
  let done = false;

  return {
    logger,
    init: () => {
      if (done) return;
      done = true;

      const {
        enableSentry = true,
        enableTracing = false,
        enableReportPageLoaded = enableTracing,
        tracesSampleRate = 0.2,
      } = options;

      if (!enableSentry) {
        logger.info('Sentry disabled via options.');
        collectDeviceContext();
        return;
      }

      const sentryDsn = getPublicSentryDsn();

      const release = getPublicCommitSha();

      const enabled = initSentry({
        appName,
        dsn: sentryDsn,
        release,
        sendDefaultPii: true,
        enableLogs: true,
        captureConsoleLogs: true,
        enableTracing,
        enableReportPageLoaded,
        tracesSampleRate,
      });

      setSentryTag('app_surface', 'web');

      if (enabled) {
        logger.info('Sentry error tracking initialized.');
      } else {
        logger.info('Sentry DSN missing. Remote error tracking is disabled.');
      }

      collectDeviceContext();
    },
    setUser: (userId) => setSentryUser(userId ? { id: userId } : null),
    captureError,
  };
};
